import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowRight, Check } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import Footer from "@/components/Footer";
import ListingCard from "@/components/ListingCard";

export default function LandingPage({ config }) {
  const {
    seoTitle, seoDesc, seoKeywords, path, jsonLd, heroImage, eyebrow,
    title, titleAccent, subtitle, intro = [], highlights = [],
    island, keywords, emptyText, ctaLabel = "Browse all experiences", ctaTo = "/browse",
  } = config;

  const [tours, setTours] = useState(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        if (island) {
          const { data } = await api.get("/listings", { params: { island } });
          if (active) setTours(data);
        } else {
          const { data } = await api.get("/listings");
          const kw = (keywords || []).map((k) => k.toLowerCase());
          const filtered = data.filter((l) => {
            const hay = [l.title, l.description, l.category, (l.amenities || []).join(" ")]
              .join(" ").toLowerCase();
            return kw.some((k) => hay.includes(k));
          });
          if (active) setTours(filtered);
        }
      } catch {
        if (active) setTours([]);
      }
    };
    load();
    return () => { active = false; };
    // eslint-disable-next-line
  }, [path]);

  return (
    <div className="min-h-screen bg-[#060810]">
      <Seo title={seoTitle} description={seoDesc} keywords={seoKeywords} path={path} jsonLd={jsonLd} />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <img src={heroImage} alt={title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060810]/75 via-[#060810]/80 to-[#060810]" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 on-dark">
          <p className="text-xs font-mono-caption uppercase tracking-[0.35em] text-cyan-400 mb-4">{eyebrow}</p>
          <h1 className="font-heading font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl">
            {title} {titleAccent && <span className="text-gradient-warm">{titleAccent}</span>}
          </h1>
          <p className="text-slate-300 text-base md:text-lg mt-6 max-w-2xl leading-relaxed">{subtitle}</p>
          {highlights.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-7">
              {highlights.map((h) => (
                <span key={h} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full glass text-sm text-slate-200">
                  <Check className="w-3.5 h-3.5 text-emerald-400" /> {h}
                </span>
              ))}
            </div>
          )}
          <Link to={ctaTo} className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-full btn-neon font-semibold">
            {ctaLabel} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Intro copy */}
        {intro.length > 0 && (
          <section className="py-14 max-w-3xl">
            <div className="space-y-4 text-slate-300 leading-relaxed text-base">
              {intro.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </section>
        )}

        {/* Tours */}
        <section className="pb-20">
          <h2 className="font-heading font-extrabold text-white text-3xl sm:text-4xl mb-8">
            {tours && tours.length > 0 ? "Top experiences" : "Experiences"}
          </h2>
          {tours === null ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>
          ) : tours.length === 0 ? (
            <div className="glass rounded-2xl p-10 text-center">
              <p className="text-slate-400">{emptyText || "New experiences are coming soon. Check back shortly."}</p>
              <Link to="/browse" className="inline-flex items-center gap-2 mt-5 px-5 py-3 rounded-full glass text-cyan-300 hover:text-white font-semibold">
                Explore everything on Carivisor <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="landing-tours">
              {tours.map((l) => <ListingCard key={l.id} listing={l} />)}
            </div>
          )}
        </section>
      </div>

      <Footer />
    </div>
  );
}
